import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  ReactNode,
} from 'react';
import { clearClientAuthStorage } from '../services/clientAuthStorage';
import { securityService, User } from '../services/securityService';

// ===============================
// TIPOS DE CONTEXTO
// ===============================

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  isAuthenticated: boolean;
  /** Recarga nombre/rol desde el API, o aplica un usuario ya obtenido (evita doble GET). */
  refreshUser: (prefetched?: User | null) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// ===============================
// PROVIDER COMPONENT
// ===============================

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      try {
        if (!securityService.hasStoredSession()) {
          setUser(null);
          return;
        }

        const token = await securityService.getValidAccessToken();
        if (!token) {
          clearClientAuthStorage();
          setUser(null);
          return;
        }

        const session = await securityService.fetchProfileSession();
        if (session) {
          setUser(session.user);
          return;
        }

        clearClientAuthStorage();
        setUser(null);
      } catch (error) {
        console.error('Error inicializando auth:', error);
        clearClientAuthStorage();
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();
  }, []);

  const login = async (username: string, password: string): Promise<void> => {
    try {
      setIsLoading(true);
      const response = await securityService.login(username, password);
      setUser(response.user);
    } catch (error) {
      console.error('Error en login:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async (): Promise<void> => {
    await securityService.logout();
    setUser(null);
  };

  const refreshUser = useCallback(async (prefetched?: User | null) => {
    if (prefetched) {
      setUser(prefetched);
      localStorage.setItem('user', JSON.stringify(prefetched));
      return;
    }
    const raw = localStorage.getItem('user');
    if (!raw) return;
    let id: number;
    try {
      const parsed = JSON.parse(raw);
      id = parsed.id ?? parsed.Id;
    } catch {
      return;
    }
    if (!id) return;
    try {
      const fresh = await securityService.getUser(id);
      if (fresh) {
        setUser(fresh);
        localStorage.setItem('user', JSON.stringify(fresh));
      }
    } catch (e) {
      console.error('Error refrescando usuario:', e);
    }
  }, []);

  const isAuthenticated = !!user;

  const value: AuthContextType = {
    user,
    isLoading,
    login,
    logout,
    isAuthenticated,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth debe ser usado dentro de un AuthProvider');
  }
  return context;
}
